import * as React from 'react'

import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import reverse from 'lodash/reverse'
import sortBy from 'lodash/sortBy'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'

import type Employee from 'modules/employees/store/models/Employee'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import type LeaveModel from 'modules/timeoff/store/models/Leave'

import Section, { LeaveSectionType } from './Section'
import SkeletonLoader from './Section/SkeletonLoader'

interface Props {
  employee: Employee
  year: number
}

const LeavesList = ({ employee, year }: Props) => {
  const { fetchData, currentAndFutureLeaves } = useLeaves()

  const props = {
    employee,
    year,
  }

  const { loading } = useAsyncData(() => fetchData(employee), [year, employee.id])

  const groupBy = (type: LeaveSectionType): LeaveModel[] => {
    const groupedLeaves = employeeLeaves().filter((leave) => {
      const today = moment()
      const beggining = moment(leave.attributes.start_on).startOf('day')
      const finish = moment(leave.attributes.finish_on).endOf('day')

      const isCurrent =
        isNaN(finish as unknown as number) ||
        (today.isSameOrAfter(beggining) && today.isSameOrBefore(finish))

      const isApproved = leave.attributes.approved
      const upcoming = today.isBefore(beggining)
      const isMedical = leave.leaveType?.isMedicalLeave

      switch (type) {
        case 'medical':
          return (isCurrent || upcoming) && isMedical

        case 'upcoming':
          return (isCurrent || upcoming) && isApproved && !isMedical

        case 'past':
          return today.isAfter(finish) && isApproved

        case 'rejected':
          return leave.attributes.approved === false

        case 'pending_review':
          return !isMedical && (isNull(isApproved) || isUndefined(isApproved))

        default:
          return true
        // return all
      }
    })

    if (type === 'past') {
      return reverse(sortBy(groupedLeaves, (l: LeaveModel) => l.attributes.finish_on))
    }

    return sortBy(groupedLeaves, (l: LeaveModel) => l.attributes.start_on)
  }

  const employeeLeaves = (): LeaveModel[] => {
    return currentAndFutureLeaves.filter({
      employee_id: employee.attributes.id,
    })
  }

  if (loading) return <SkeletonLoader />

  const medical = groupBy('medical')
  const past = groupBy('past')
  const pendingReview = groupBy('pending_review')
  const rejected = groupBy('rejected')
  const upcoming = groupBy('upcoming')

  return (
    <>
      {medical.length > 0 && <Section leaves={medical} type="medical" {...props} />}
      {pendingReview.length > 0 && (
        <Section leaves={pendingReview} type="pending_review" {...props} />
      )}
      {upcoming.length > 0 && <Section leaves={upcoming} type="upcoming" {...props} />}
      <Section leaves={past} type="past" {...props} />
      {rejected.length > 0 && <Section leaves={rejected} type="rejected" {...props} />}
    </>
  )
}

export default observer(LeavesList)
